import React, { useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import SiteNav from '../SiteNav';
import FooterSection from '../Footer/FooterSection';
import PaymentPlan from '../PaymentPlan';
import InformationalCard from '../Cards/InformationalCard';
import MapPlusButton from '../Cards/MapPlusButton';
import HelpSection from '../HelpSection';
import LockCopy from '../LockCopy';
import PerkCard from '../PerkCard';
import KlaviyoNewsletter from '../KlaviyoNewsletter';

import LoaderComponent from '@evdy/web-core/dist/components/shared/elements/LoaderComponent';

import { useScrollToTop } from '@evdy/web-core/dist/customHooks';
import { useGetWebleadFromCookie } from '../../customHooks';

import { nameString, formatAddress, formatTime } from '@evdy/web-core/dist/lib/utils';

import { identify, quoteIssued } from '../../klaviyoActions';
import { getPriceOperations } from '@evdy/web-redux/dist/lib/webPurchase/getPrice';
import { purchaseFlowOperations } from '@evdy/web-redux/dist/lib/webPurchase/purchaseFlow';
import { authUserOperations } from '@evdy/web-redux/dist/lib/authUser';

import styles from './PackageSummary.module.scss';

import helpIcon from '../../public/images/CardElements/Help-Filled.png';
import conciergePerkIcon from '../../public/images/icons/headset-icon.svg';
import returnThirtyPerkIcon from '../../public/images/icons/return-icon.svg';
import saveMoneyPerkIcon from '../../public/images/icons/cash-icon.svg';

import { packagePageRoutes } from '../../constants';

const { getPrices } = getPriceOperations;
const { updateWebleadData, resetWebleadUpdated } = authUserOperations;

const {
  'package-summary': packageSummaryClass,
  'summary-content': summaryContentClass,
  'payment-duration-section': paymentDurationSectionClass,
  'section-title': sectionTitleClass,
  'perk-card': perkCardClass,
  'perk-modal': perkModalClass,
  'perk-section': perkSectionClass,
  'package-card-wrapper': packageCardWrapper,
  'lock-section': lockSectionClass,
  'new-package-card': newPackageCardClass,
  'change-package-button': changePackageButtonClass,
  'provider-section': providerSectionClass,
  // user info card
  'user-info-section': userInfoSectionClass,
  'user-info-card': userInfoCardClass,
  'basic-info': basicInfoClass,
  'user-info-row': userInfoRowClass,
  'button-row': buttonRowClass,
  // get in touch
  'get-in-touch': getInTouchClass,
  // help section
  // misc
  'section-divider': sectionDividerClass,
  'right-nav-content': rightNavContentClass
} = styles;

const SectionDivider = () => {
  return <div className={sectionDividerClass} />;
};

const UserInfoCard = ({ phone, dateOfBirth, email }) => {
  return (
    <div className={userInfoCardClass}>
      <div className={userInfoRowClass}>
        <div>Birthdate</div>
        <div>{formatTime(dateOfBirth)}</div>
      </div>
      <div className={userInfoRowClass}>
        <div>Email</div>
        <div>{email}</div>
      </div>
      {!!phone && (
        <div className={userInfoRowClass}>
          <div>Phone</div>
          <div>{phone}</div>
        </div>
      )}
    </div>
  );
};

const RightNavContent = ({ buttonCopy, handleButtonClick }) => {
  return (
    <div className={rightNavContentClass}>
      <button onClick={handleButtonClick}>{buttonCopy}</button>
      <a href="https://help.everdays.com/" target="_blank" rel="noopener noreferrer">
        Help
      </a>
    </div>
  );
};

const PackageSummary = () => {
  // monthly payment choices are indexes 0 - 3,
  const [selectedPlanIdx, setSelectedPlanIdx] = useState(0);
  // single payment idx will be 11 for now
  const singlePaymentIdx = 11;
  const isSinglePaymentSelected = selectedPlanIdx === singlePaymentIdx;

  const dispatch = useDispatch();
  const { authUser = {}, isFetchingPrice, getPriceData = [], purchaseFlow = {} } = useSelector(
    ({ authUser, webPurchase }) => ({
      authUser,
      isFetchingPrice: webPurchase?.getPrice?.isFetching,
      getPriceData: webPurchase?.getPrice?.data,
      purchaseFlow: webPurchase?.purchaseFlow
    })
  );

  // assign most prioritized user data
  const { data: userData, webleadUpdated } = useMemo(() => {
    return !!authUser?.data?._id ? authUser : purchaseFlow;
  }, [authUser, purchaseFlow]);

  const { _id: userId, eFuneral = {}, dateOfBirth, email, address, phone, meta = {} } =
    userData ?? {};
  const { webLeadPhone, webLeadEmail } = meta || {};
  const { zip } = address ?? {};
  const {
    packageData: currentPackageData = {},
    funeralHomeId: currentProvider,
    paymentOption,
    defaultPackageName
  } = eFuneral;
  const { number: packageNumber = null } = currentPackageData;

  const funeralHomeData = eFuneral?.funeralHomeId || {};
  const { preneedPackages } = funeralHomeData || {};
  const { priceStructure } = preneedPackages || [];
  // formatted home prices ex. [1000, 2000, 3000]
  const homePrices = priceStructure?.slice(0, 3)?.map(el => Object.values(el)[0]);

  const amounts = homePrices?.length ? homePrices.join(',') : 'default';

  const selectedPackage = Number.isInteger(packageNumber) && getPriceData[packageNumber];
  const { data: selectedPackageMonthly = [], value: selectedPackageSinglePaymentValue } =
    selectedPackage ?? {};
  const monthlyPrice = selectedPackageMonthly[selectedPlanIdx]?.premium || 0;
  const fullPrice = selectedPackageSinglePaymentValue || 0;
  const monthlyOptions = selectedPackage?.data || [];

  // monthly pricing
  const [dollars, cents = 0] = `${monthlyPrice}`.split('.');
  // prevent trailing zeroes ($.10) from being removed
  const formattedCents = cents?.toString().length === 1 ? `${cents}0` : cents.toString(0);

  // full pricing
  const [fullPriceDollars, fullPriceCents = 0] = `${fullPrice}`.split('.');
  const formattedFullPriceCents =
    fullPriceCents?.toString().length === 1 ? `${fullPriceCents}0` : fullPriceCents.toString(0);

  // display pricing
  const displayDollars = isSinglePaymentSelected ? fullPriceDollars : dollars;
  const displayCents = isSinglePaymentSelected ? formattedFullPriceCents : formattedCents;

  const { name, packageSubtitle } = currentPackageData || {};

  // scroll to top on mount
  useScrollToTop();

  // on mount, check if weblead cookie exists and fetch existing weblead
  useGetWebleadFromCookie();

  // sync weblead with klaviyo
  useLayoutEffect(() => {
    identify({ email, zip, birthdate: dateOfBirth, phone });
    quoteIssued(name);
  }, [zip, dateOfBirth, email, phone, name]);

  // get prices/pricing plan data on mount
  useEffect(() => {
    if (userId) {
      dispatch(getPrices({ amounts, userId }));
    }
  }, [dispatch, userId, amounts]);

  // after saving paymentOption, navigate to next step
  useEffect(() => {
    if (webleadUpdated && paymentOption) {
      if (authUser?.data?._id) {
        dispatch(resetWebleadUpdated());
      } else {
        dispatch(purchaseFlowOperations.resetUpdateWeblead());
      }
      if (currentProvider?._id) {
        browserHistory.push('/b/great-choice');
      } else {
        browserHistory.push('/b/package-request');
      }
    }
  }, [authUser?.data?._id, currentProvider?._id, dispatch, paymentOption, webleadUpdated]);

  const handlePlanChange = planLength => {
    setSelectedPlanIdx(planLength);
  };

  const handleOneTimePayment = () => {
    const updatedUserBody = {
      eFuneral: {
        ...eFuneral,
        paymentOption: {
          mode: 'single',
          yearsPayable: 1,
          faceAmount: selectedPackageSinglePaymentValue,
          premiumAmount: selectedPackageSinglePaymentValue
        },
        lastScreen: {
          flow: 'everdays',
          screen: 'select payment',
          date: new Date().toISOString()
        }
      }
    };
    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  const handleMonthlyPayment = () => {
    const { years, faceAmount, premium } = monthlyOptions?.[selectedPlanIdx] || {};
    const updatedUserBody = {
      eFuneral: {
        ...eFuneral,
        paymentOption: {
          mode: 'multi',
          yearsPayable: years,
          faceAmount,
          premiumAmount: premium
        },
        lastScreen: {
          flow: 'everdays',
          screen: 'select payment',
          date: new Date().toISOString()
        }
      }
    };
    if (authUser?.data?._id) {
      dispatch(updateWebleadData(updatedUserBody));
    } else {
      dispatch(purchaseFlowOperations.updateWeblead(userId, updatedUserBody));
    }
  };

  const handleSelectPlan = () => {
    if (selectedPlanIdx === singlePaymentIdx) {
      handleOneTimePayment();
    } else {
      handleMonthlyPayment();
    }
  };

  const shouldRenderLoader = isFetchingPrice;

  let submitButtonCopy = '';
  if (currentProvider?._id) {
    submitButtonCopy = `Pay $${displayDollars}.${displayCents} ${
      selectedPlanIdx === singlePaymentIdx ? 'for package' : 'per month'
    }`;
  } else {
    submitButtonCopy = 'Request package';
  }

  return (
    <div className={packageSummaryClass}>
      <SiteNav
        rightNavContent={() => (
          <RightNavContent buttonCopy={submitButtonCopy} handleButtonClick={handleSelectPlan} />
        )}
      />
      <div className={summaryContentClass}>
        <div className={paymentDurationSectionClass}>
          <PaymentPlan
            {...{
              defaultPackageName,
              selectedPlanIdx,
              singlePaymentIdx,
              monthlyOptions,
              handlePlanChange,
              handleSelectPlan,
              submitButtonCopy,
              displayDollars,
              displayCents,
              isSinglePaymentSelected
            }}
          />
          <LockCopy
            color="red"
            copy="With Everdays, buying your funeral now locks in prices so you’re protected from inflation."
          />
        </div>
        <SectionDivider />
        <div className={packageCardWrapper}>
          <div className={newPackageCardClass}>
            <h3>{name}</h3>
            <p>{packageSubtitle}</p>
            <button
              onClick={() => {
                window.location.href = packagePageRoutes[packageNumber];
              }}
            >
              View all details
            </button>
            <button
              onClick={() => {
                window.location.href = '/packages';
              }}
              className={changePackageButtonClass}
            >
              Change package
            </button>
          </div>
        </div>
        {currentProvider?._id && (
          <>
            <SectionDivider />
            <div className={sectionTitleClass}>
              <h3>Funeral home provider</h3>
            </div>
            <div className={providerSectionClass}>
              <MapPlusButton
                isSelected
                decoratorCopy={currentProvider?.priceListingGPLUrl && 'PREFERRED'}
                title={currentProvider?.name}
                rating={currentProvider?.ratingsInfo?.average ?? null}
                clickableRowCopy="View General Price List"
                clickableRowAction={() => {
                  window.open(currentProvider?.priceListingGPLUrl, '_blank', 'noopener');
                }}
                hideClickableRow={!currentProvider?.priceListingGPLUrl}
                locationAddress={currentProvider?.address || {}}
                buttonCopy="Change provider"
                handleButtonClick={() => browserHistory.push('/b/provider')}
              />
            </div>
          </>
        )}
        <SectionDivider />
        <div className={sectionTitleClass}>
          <h3>Our perks</h3>
        </div>
        <div className={perkSectionClass}>
          <PerkCard
            perkIcon={saveMoneyPerkIcon}
            perkTitle="Payment protection"
            perkSubtitle="Your funds are protected in an insurance policy issued by Homesteaders Life Company – so your family will have what they need."
            buttonCopy="Here's why & how"
            modalHeaderCopy="Rest assured your funds are safe"
            modalBodyCopy="When you buy a package from Everdays, your payments are put into a life insurance policy issued by 100 year old, national and top-rated insurance leader, Homesteaders Life Company – to ensure your money is protected and available for your family when they need it."
          />
          <PerkCard
            perkIcon={returnThirtyPerkIcon}
            perkTitle="No stress guarantee"
            perkSubtitle="We provide a 30-day free look period. If you change your mind, you can cancel your purchase and we’ll issue a full refund – no questions asked."
            buttonCopy="Here's why & how"
            modalHeaderCopy="We've got you covered"
            modalBodyCopy={[
              'It’s important to us that you’re completely satisfied with your purchase.',
              <br />,
              <br />,
              'We provide a 30-day free look period that begins at the time of purchase. If you change your mind at any time during your free look, you can return your package and we’ll issue you a full refund - no questions asked.'
            ]}
          />
          <PerkCard
            perkIcon={conciergePerkIcon}
            perkTitle="Care team support"
            perkSubtitle="For personal assistance with package details, we’re here to help with anything you need."
            buttonCopy="Here's why & how"
            modalHeaderCopy="We're here for you"
            modalBodyCopy={[
              'We want you to have the best experience with Everdays that you possibly can, and we’re here to help make sure that happens.',
              <br />,
              <br />,
              'Whether it’s live app support or talking with one of our planning coaches about package details before or after your purchase, our care team has the personal assistance you’re looking for.'
            ]}
          />
        </div>
        <SectionDivider />
        <div className={userInfoSectionClass}>
          <h3>Your information</h3>
          <UserInfoCard
            {...{ phone: phone || webLeadPhone, dateOfBirth, email: email || webLeadEmail }}
          />
        </div>
        <div className={getInTouchClass}>
          <InformationalCard
            isBorderless
            icon={helpIcon}
            title="Don't see exactly what you're looking for?"
            body="If you’re looking for something different or would like to customize your package options, let us know. Our support team is here to help make sure you get what you want."
            ctaCopy="Get in touch"
            ctaHandler={() => {}}
            customButtonClass="intercom-chat"
          />
        </div>
      </div>
      <HelpSection />
      <KlaviyoNewsletter />
      <FooterSection />
      {shouldRenderLoader && <LoaderComponent />}
    </div>
  );
};

export default PackageSummary;
